<script lang="ts">
	import type { Device } from "luxedo-data"
	import { ToggleSwitch } from "misc-ui"
	import { Responsive } from "responsive"

	import { DeviceSpotlightManager } from "luxedo-data"
	import { CalibrationController } from "../CalibrationController.svelte"
	import { DaylightWarning } from "../DaylightWarning.svelte"

	interface Props {
		device: Device
	}

	let { device }: Props = $props()

	let isMobile = $derived(Responsive.isMobile)
	let togglingSpotlight = $state(false)
	let spotlightActive = $state(false)

	DeviceSpotlightManager.subscribe((ctx) => (spotlightActive = ctx[device.id]))

	async function beginCalibration() {
		if (DaylightWarning.needsWarn) {
			// Check if it's daylight and get user confirmation
			const isDark = await DaylightWarning.warn()
			if (!isDark) return cancelCalibration()
		}

		if (spotlightActive) {
			await disableSpotlight()
		}

		await CalibrationController.calibrate(device)
	}

	function cancelCalibration() {
		CalibrationController.cancel(device)
	}

	async function activateSpotlight() {
		togglingSpotlight = true
		await DeviceSpotlightManager.activateSpotlight(device)
		togglingSpotlight = false
	}

	async function disableSpotlight() {
		togglingSpotlight = true
		await DeviceSpotlightManager.deactivateSpotlight(device)
		togglingSpotlight = false
	}

	async function toggleSpotlight() {
		if (spotlightActive) {
			return await disableSpotlight()
		} else {
			return await activateSpotlight()
		}
	}
</script>

<div id="calibration-instructions" class:isMobile>
	<div class="calibration-step">
		<span class="step_number">1</span>
		Make sure your device is in position before starting calibration process.
	</div>

	<div class="calibration-step">
		<span class="step_number">2</span>
		Ensure your projection surface is protected from any interfering light sources such as the sun, christmas
		lights or lights inside your home.
	</div>

	<div class="calibration-step">
		<span class="step_number">3</span>
		Do not block or stand in front of the device during the calibration process.
	</div>

	<div class="advanced-option-spotlight">
		<span class="label">Enable Projector Spotlight </span>
		<ToggleSwitch
			isEnabled={spotlightActive}
			disabled={togglingSpotlight}
			onUpdate={() => toggleSpotlight()}
			info="Enabling the projector spotlight will trigger your device to project white light allowing you to better visualize and adjust your device's position."
		/>
	</div>
</div>

<div class="button-container">
	<button onclick={cancelCalibration} class="link-button">Cancel</button>
	<button onclick={beginCalibration}>Calibrate</button>
</div>

<style>
	.step_number {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-width: 3rem;
		width: 3rem;
		height: 3rem;
		border: 1px solid var(--color-text);
		color: var(--color-text-light);

		border-radius: 99px;
		margin-right: 1rem;
		padding-bottom: 0.5rem;
		font-size: 2rem;
	}

	.calibration-step {
		margin-bottom: 1rem;
		display: flex;
		flex-direction: row;
		color: var(--color-text);
		align-items: center;
	}

	#calibration-instructions {
		max-width: 36rem;
	}

	.advanced-option-spotlight {
		position: absolute;
		top: -2.25rem;
		right: 1rem;
		color: var(--color-text);
		display: flex;
		flex-direction: row;
		align-items: center;
		z-index: 15;
	}

	.advanced-option-spotlight .label {
		font-size: var(--h3);
		color: var(--color-text);
		line-height: 1em;
		margin-right: 0.5rem;
	}

	.isMobile .step_number {
		font-size: var(--h2);

		min-width: 2rem;
		width: 2rem;
		height: 2rem;
	}

	.isMobile .calibration-step {
		font-size: var(--h3);
		line-height: 1.25em;
	}

	.isMobile .advanced-option-spotlight {
		right: unset;
		top: 1.25rem;
		left: 0rem;
	}
</style>
